/**
 * Form primitives — design/09 §8: input, select, combobox, checkbox, radio, switch,
 * slider. Radix-headless where Radix has a primitive, bespoke where it does not
 * (combobox), Tailwind + tokens for every pixel of appearance.
 *
 * Cross-cutting rules applied to every control here:
 *   · focus ring from `FOCUS_RING`, never removed (§12.8)
 *   · label is REQUIRED and is an ICU key, never a string (G6)
 *   · error, help and required state are wired with `aria-describedby` /
 *     `aria-invalid`, so the message is announced rather than merely coloured
 *   · no fixed widths — G6 budgets 40% string expansion
 *   · numeric input uses `type-numeric` (IBM Plex Mono, §4.3) so a column of inputs
 *     in a grid aligns vertically like the cells around it
 */

import { forwardRef, useEffect, useId, useMemo, useState } from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as RadixRadio from '@radix-ui/react-radio-group';
import * as RadixSelect from '@radix-ui/react-select';
import * as RadixSwitch from '@radix-ui/react-switch';
import * as RadixSlider from '@radix-ui/react-slider';
import * as RadixLabel from '@radix-ui/react-label';
import { Check, ChevronDown, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FOCUS_RING, FOCUS_RING_INSET } from './focus';
import { displayNumberFormat } from '@/lib/format';

// ── the shell every field shares ─────────────────────────────────────────────

export interface FieldShellProps {
  /** ICU key. G6 — there is no `label: string` on any control in this file. */
  readonly labelKey: string;
  readonly helpKey?: string;
  /** ICU key for the validation message. Presence means invalid. */
  readonly errorKey?: string;
  readonly required?: boolean;
  /** Unit of measure, shown beside the label rather than repeated in the control
   *  (design/09 §4.3). */
  readonly unitKey?: string;
  /**
   * The label is still the control's accessible name, but is not drawn — for a control whose
   * heading already says what it is, as in a one-line `ScopeChooser` (ADR 9036). The required
   * mark goes with it: it annotates a visible label, and there is none to annotate.
   */
  readonly labelHidden?: boolean;
  /** The control's `id`, when something outside the shell must name it (`htmlFor`, a test hook). */
  readonly id?: string;
  /** Replaces the label's look — for a field set inside a bar whose labels are already styled. */
  readonly labelClassName?: string;
  /** Added to the shell's own box (a width, for a field set among others in a row). */
  readonly className?: string;
  readonly children: (ids: { controlId: string; describedBy: string | undefined; invalid: boolean }) => React.ReactNode;
}

export function FieldShell({
  labelKey, helpKey, errorKey, required, unitKey, labelHidden = false, id, labelClassName, className, children,
}: FieldShellProps): React.JSX.Element {
  const { t } = useTranslation();
  const ownId = useId();
  const controlId = id ?? ownId;
  const helpId = `${controlId}-help`;
  const errorId = `${controlId}-error`;
  const describedBy = [helpKey ? helpId : null, errorKey ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={className === undefined ? 'flex flex-col gap-1' : `flex flex-col gap-1 ${className}`}>
      <RadixLabel.Root htmlFor={controlId} className={labelHidden ? 'sr-only' : labelClassName ?? 'text-label text-ink-secondary inline-flex items-baseline gap-1'}>
        <span>{t(labelKey)}</span>
        {/**
          * `ink-secondary`, NOT `ink-muted` — design/09 §12.7, and measured rather than assumed.
          * `ink-muted` is 2.17:1 against its worst surface in light and 3.52:1 in dark; neither
          * clears WCAG 2.2 AA's 4.5:1 for body text, and there is no opacity or surface that buys
          * it back. `ink-secondary` is 4.54:1 at ITS worst (sunken, light) and 7.37:1 at its worst
          * in dark. ADR 0092.
          *
          * This path existed for months and no audited route exercised it, because no screen had
          * yet passed `unitKey` to a control. axe found it the day ch05's calculators did.
          */}
        {unitKey && <span className="text-caption text-ink-secondary">{t(unitKey)}</span>}
        {required && !labelHidden && <span aria-hidden className="text-status-critical">{t('field.requiredMark')}</span>}
      </RadixLabel.Root>
      {children({ controlId, describedBy, invalid: errorKey !== undefined })}
      {helpKey && <p id={helpId} className="text-caption text-ink-secondary">{t(helpKey)}</p>}
      {errorKey && (
        // §12.6 — the error carries text, not only the red border. Colour is never the
        // sole carrier of meaning.
        <p id={errorId} role="alert" className="text-caption text-status-critical">{t(errorKey)}</p>
      )}
    </div>
  );
}

const CONTROL_BASE = [
  'bg-surface-raised text-ink-primary border border-hairline rounded-control',
  'h-8 px-2 text-body font-ui',
  'placeholder:text-ink-muted',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  // Same misspelling as the destructive button carried: the group is `status`, and
  // `border-color-status-critical` emitted nothing — so an invalid field drew the ordinary
  // hairline and the error was announced but never shown.
  'aria-[invalid=true]:border-status-critical',
  FOCUS_RING,
].join(' ');

// ── input ────────────────────────────────────────────────────────────────────

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  readonly labelKey: string;
  readonly helpKey?: string;
  readonly errorKey?: string;
  readonly unitKey?: string;
  readonly placeholderKey?: string;
  /** design/09 §4.3 — a numeric input is set in IBM Plex Mono so a column of them
   *  aligns with the numbers around it. */
  readonly numeric?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { labelKey, helpKey, errorKey, unitKey, placeholderKey, numeric = false, required, className, ...rest }, ref,
) {
  const { t } = useTranslation();
  return (
    <FieldShell
      labelKey={labelKey}
      {...(helpKey === undefined ? {} : { helpKey })}
      {...(errorKey === undefined ? {} : { errorKey })}
      {...(unitKey === undefined ? {} : { unitKey })}
      required={required === true}
    >
      {({ controlId, describedBy, invalid }) => (
        <input
          ref={ref}
          id={controlId}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          placeholder={placeholderKey ? t(placeholderKey) : undefined}
          className={[
            CONTROL_BASE,
            // A number is short, and a field's width tells the planner how much goes in
            // it: stretched to a 1140px column a numeric input read as a text area (audit v4
            // G-21). Capped, it keeps its column's start edge and stops where a figure ends.
            numeric ? 'font-numeric text-numeric text-end tabular-nums max-w-xs' : '',
            className ?? '',
          ].join(' ')}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

// ── select ───────────────────────────────────────────────────────────────────

/**
 * One option: a catalogue KEY, or a label already resolved elsewhere.
 *
 * `label` is for text this build does not own — a platform vocabulary resolved against the
 * caller's language (ADR 0057), a run described in a sentence the caller already translated.
 * Passing such text as `labelKey` rendered it as a missing key, `⟦…⟧`, because the select
 * translates every key it is given. One or the other, never both.
 */
/** The text an option shows: its resolved `label`, or its key translated. */
export function optionText(option: SelectOption, t: (key: string) => string): string {
  return option.label ?? t(option.labelKey);
}

export type SelectOption = {
  readonly value: string;
  readonly disabled?: boolean;
} & (
  | { readonly labelKey: string; readonly label?: never }
  | { readonly label: string; readonly labelKey?: never }
);

export interface SelectProps {
  readonly labelKey: string;
  /** Keep the label as the accessible name without drawing it — see `FieldShellProps`. */
  readonly labelHidden?: boolean;
  readonly helpKey?: string;
  readonly errorKey?: string;
  readonly placeholderKey?: string;
  readonly options: readonly SelectOption[];
  readonly value?: string;
  readonly onValueChange?: (value: string) => void;
  readonly required?: boolean;
  readonly disabled?: boolean;
}

export function Select({
  labelKey, labelHidden = false, helpKey, errorKey, placeholderKey, options, value, onValueChange, required, disabled,
}: SelectProps): React.JSX.Element {
  const { t } = useTranslation();
  // The chosen option's whole text, as the trigger's tooltip: the value is held to one line and
  // ellipsised (below), so a long option — a run's market · period · channel · time · id — would
  // otherwise be cut with no way to read the rest (usability audit D1).
  const chosen = value === undefined ? undefined : options.find((option) => option.value === value);
  const chosenText = chosen === undefined ? undefined : optionText(chosen, t);
  return (
    <FieldShell
      labelKey={labelKey}
      labelHidden={labelHidden}
      {...(helpKey === undefined ? {} : { helpKey })}
      {...(errorKey === undefined ? {} : { errorKey })}
      required={required === true}
    >
      {({ controlId, describedBy, invalid }) => (
        <RadixSelect.Root
          {...(value === undefined ? {} : { value })}
          {...(onValueChange === undefined ? {} : { onValueChange })}
          {...(disabled === undefined ? {} : { disabled })}
        >
          <RadixSelect.Trigger
            id={controlId}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            // The chosen option whole (usability audit D1/D9): the value below is cut to one line.
            {...(chosenText === undefined ? {} : { title: chosenText })}
            className={`${CONTROL_BASE} inline-flex items-center justify-between gap-2`}
          >
            {/* The value is held to one line inside a control whose height is fixed at
                `h-8`. Without this it wrapped: a long option in a narrow column became
                two or three lines in a 32px box and painted over the field's own label
                above it and its help text below — measured overlapping both by 20px on
                `/inseason/markdown`'s market picker. The chevron keeps its size so the
                control still reads as a select once the text is ellipsised. */}
            <span className="min-w-0 truncate">
              <RadixSelect.Value placeholder={placeholderKey ? t(placeholderKey) : undefined} />
            </span>
            <RadixSelect.Icon className="shrink-0"><ChevronDown size={14} aria-hidden /></RadixSelect.Icon>
          </RadixSelect.Trigger>
          <RadixSelect.Portal>
            <RadixSelect.Content
              position="popper"
              sideOffset={4}
              className="bg-surface-raised border-popover-border z-popover max-h-[var(--radix-select-content-available-height)] rounded-control border shadow-popover"
            >
              <RadixSelect.Viewport className="p-1">
                {options.map((option) => (
                  <RadixSelect.Item
                    key={option.value}
                    value={option.value}
                    {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
                    className={`text-body text-ink-primary data-[highlighted]:bg-surface-sunken flex cursor-default items-center gap-2 rounded-chip px-2 py-1 data-[disabled]:opacity-50 ${FOCUS_RING_INSET}`}
                  >
                    <RadixSelect.ItemIndicator><Check size={12} aria-hidden /></RadixSelect.ItemIndicator>
                    <RadixSelect.ItemText>{optionText(option, t)}</RadixSelect.ItemText>
                  </RadixSelect.Item>
                ))}
              </RadixSelect.Viewport>
            </RadixSelect.Content>
          </RadixSelect.Portal>
        </RadixSelect.Root>
      )}
    </FieldShell>
  );
}

// ── combobox ─────────────────────────────────────────────────────────────────

/**
 * Bespoke rather than a dependency. Radix has no combobox, and the obvious candidates
 * (`cmdk`, `downshift`) are not on the design/09 §10.1 register — adding one would
 * need G4 approval for a component we can write in eighty lines.
 *
 * ARIA 1.2 combobox pattern: `role="combobox"` on the input, `aria-expanded`,
 * `aria-controls`, `aria-activedescendant`, and full keyboard operation. This is the
 * control most often shipped as a mouse-only filter box, which G2 §2.6 forbids.
 */
export interface ComboboxProps {
  readonly labelKey: string;
  readonly helpKey?: string;
  readonly errorKey?: string;
  readonly placeholderKey?: string;
  readonly options: readonly SelectOption[];
  readonly value?: string;
  readonly onValueChange?: (value: string) => void;
  readonly emptyKey?: string;
  /**
   * The remaining props are for a combobox that COLLECTS values — a multi-select (the ranked
   * bar's `multi` filter, `src/planning/ranked-cap.tsx`) — and are all opt-in: absent, the
   * control is the single-value combobox it always was.
   *
   * `collect`: choosing an option reports it and CLEARS the box, leaving the list open and the
   * focus where it is, so the next value can be typed at once. The caller takes the chosen value
   * out of `options` and draws it as a chip. Nothing is shown in the box as "the value", because
   * there is no single one.
   */
  readonly collect?: boolean;
  /**
   * At most this many matches are drawn; the rest are reached by typing, and the list says so
   * with `moreKey` (ICU arguments `shown`, `matching`). Thousands of rows in a popover is slow to
   * draw and no faster to read — the filter is the way in, not the scrollbar.
   */
  readonly maxOptions?: number;
  readonly moreKey?: string;
  /** Match what is typed against the option's value (a code) as well as its words. */
  readonly matchValue?: boolean;
  /** The control's `id`, the label's look and the shell's box — see {@link FieldShellProps}. */
  readonly id?: string;
  readonly labelHidden?: boolean;
  readonly labelClassName?: string;
  readonly className?: string;
  /** Extra `data-*` attributes on the input — a hook for a caller that finds its control by one. */
  readonly inputData?: Readonly<Record<`data-${string}`, string>>;
}

export function Combobox({
  labelKey, helpKey, errorKey, placeholderKey, options, value, onValueChange, emptyKey = 'field.noMatches',
  collect = false, maxOptions, moreKey, matchValue = false, id, labelHidden, labelClassName, className, inputData,
}: ComboboxProps): React.JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();

  const matching = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (needle === '') return options;
    return options.filter((o) => optionText(o, t).toLocaleLowerCase().includes(needle)
      || (matchValue && o.value.toLocaleLowerCase().includes(needle)));
  }, [options, query, t, matchValue]);
  // What is drawn, and what the keyboard moves over: the same list, so End never lands on a row
  // that is not there.
  const filtered = useMemo(
    () => (maxOptions === undefined ? matching : matching.slice(0, maxOptions)),
    [matching, maxOptions],
  );
  const hidden = matching.length - filtered.length;

  // The option the arrow keys are on stays inside the list's window — with hundreds of rows the
  // highlight would otherwise walk out of sight. `scrollIntoView` is absent in a test DOM.
  useEffect(() => {
    if (!open) return;
    document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, listId]);

  const commit = (option: SelectOption | undefined) => {
    if (!option) return;
    onValueChange?.(option.value);
    if (collect) {
      // Ready for the next value: empty box, same list, same place in it.
      setQuery('');
      setActiveIndex((i) => Math.max(0, Math.min(i, filtered.length - 2)));
      return;
    }
    setQuery(optionText(option, t));
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); setOpen(true); setActiveIndex((i) => Math.min(i + 1, filtered.length - 1)); break;
      case 'ArrowUp': event.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); break;
      case 'Home': if (open) { event.preventDefault(); setActiveIndex(0); } break;
      case 'End': if (open) { event.preventDefault(); setActiveIndex(filtered.length - 1); } break;
      case 'Enter': if (open) { event.preventDefault(); commit(filtered[activeIndex]); } break;
      case 'Escape': setOpen(false); break;
      default: break;
    }
  };

  return (
    <FieldShell
      labelKey={labelKey}
      {...(helpKey === undefined ? {} : { helpKey })}
      {...(errorKey === undefined ? {} : { errorKey })}
      {...(id === undefined ? {} : { id })}
      {...(labelHidden === undefined ? {} : { labelHidden })}
      {...(labelClassName === undefined ? {} : { labelClassName })}
      {...(className === undefined ? {} : { className })}
    >
      {({ controlId, describedBy, invalid }) => (
        <div className="relative flex flex-col">
          <input
            {...inputData}
            id={controlId}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && filtered[activeIndex] ? `${listId}-${activeIndex}` : undefined}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            value={query || (() => {
              const chosen = value ? options.find((o) => o.value === value) : undefined;
              return chosen === undefined ? '' : optionText(chosen, t);
            })()}
            placeholder={placeholderKey ? t(placeholderKey) : undefined}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(0); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
            className={CONTROL_BASE}
          />
          {open && (
            <ul
              id={listId}
              role="listbox"
              // `max-h-[16rem]`, not `max-h-64`. The token spacing scale has no `64` key
              // (it ends at 16 = 64px), so the class emitted nothing and the list had no
              // ceiling: an unfiltered combobox ran the full height of its options with
              // `overflow-auto` never engaging.
              className="bg-surface-raised border-popover-border z-popover absolute top-full mt-1 max-h-[16rem] w-full overflow-auto rounded-control border p-1 shadow-popover"
            >
              {/* `ink-secondary` — ADR 0092. This sentence is the whole of what the control
                  has to say when nothing matches; it cannot be the least readable thing on it. */}
              {filtered.length === 0 && <li className="text-caption text-ink-secondary px-2 py-1">{t(emptyKey)}</li>}
              {filtered.map((option, index) => (
                <li
                  key={option.value}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(e) => { e.preventDefault(); commit(option); }}
                  className={`text-body text-ink-primary cursor-default rounded-chip px-2 py-1 ${index === activeIndex ? 'bg-surface-sunken' : ''}`}
                >
                  {optionText(option, t)}
                </li>
              ))}
              {/* Not an option: the arrow keys never reach it and it cannot be chosen. It says how
                  to reach the rest — by typing — inside the list the planner is reading. */}
              {hidden > 0 && moreKey !== undefined && (
                <li role="presentation" className="text-caption text-ink-secondary px-2 py-1" data-combobox-more="">
                  {t(moreKey, { shown: filtered.length, matching: matching.length })}
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </FieldShell>
  );
}

// ── checkbox, radio, switch ──────────────────────────────────────────────────

export interface CheckboxProps {
  readonly labelKey: string;
  /** ICU arguments for `labelKey` — what, specifically, this box selects. */
  readonly labelValues?: Readonly<Record<string, string | number>>;
  /**
   * The label is read to assistive technology and not drawn — for a checkbox in a table row,
   * where the row itself is what a sighted planner reads. Audit v4 G-13: "Select exception"
   * printed on every row pushed the first real column 540px in and repeated one phrase down
   * the table. The label is never REMOVED: a checkbox with no accessible name is a defect.
   */
  readonly labelHidden?: boolean;
  readonly checked?: boolean | 'indeterminate';
  readonly onCheckedChange?: (checked: boolean) => void;
  readonly disabled?: boolean;
}

export function Checkbox({
  labelKey, labelValues, labelHidden = false, checked, onCheckedChange, disabled,
}: CheckboxProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <RadixCheckbox.Root
        id={id}
        {...(checked === undefined ? {} : { checked })}
        {...(onCheckedChange === undefined ? {} : { onCheckedChange: (c: boolean | 'indeterminate') => onCheckedChange(c === true) })}
        disabled={disabled}
        className={`border-hairline bg-surface-raised data-[state=checked]:bg-ink-primary data-[state=checked]:border-ink-primary flex size-4 items-center justify-center rounded-chip border disabled:opacity-50 ${FOCUS_RING}`}
      >
        <RadixCheckbox.Indicator className="text-surface-raised">
          {checked === 'indeterminate' ? <Minus size={10} aria-hidden /> : <Check size={10} aria-hidden />}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      <RadixLabel.Root htmlFor={id} className={labelHidden ? 'sr-only' : 'text-body text-ink-primary'}>
        {t(labelKey, labelValues ?? {})}
      </RadixLabel.Root>
    </div>
  );
}

export interface RadioGroupProps {
  readonly labelKey: string;
  readonly options: readonly SelectOption[];
  readonly value?: string;
  readonly onValueChange?: (value: string) => void;
}

export function RadioGroup({ labelKey, options, value, onValueChange }: RadioGroupProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-label text-ink-secondary">{t(labelKey)}</legend>
      <RadixRadio.Root
        {...(value === undefined ? {} : { value })}
        {...(onValueChange === undefined ? {} : { onValueChange })}
        className="flex flex-col gap-2"
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadixRadio.Item
              id={option.value}
              value={option.value}
              disabled={option.disabled}
              className={`border-hairline bg-surface-raised data-[state=checked]:border-ink-primary flex size-4 items-center justify-center rounded-full border disabled:opacity-50 ${FOCUS_RING}`}
            >
              <RadixRadio.Indicator className="bg-ink-primary size-2 rounded-full" />
            </RadixRadio.Item>
            <RadixLabel.Root htmlFor={option.value} className="text-body text-ink-primary">{optionText(option, t)}</RadixLabel.Root>
          </div>
        ))}
      </RadixRadio.Root>
    </fieldset>
  );
}

export interface SwitchProps {
  readonly labelKey: string;
  readonly checked?: boolean;
  readonly onCheckedChange?: (checked: boolean) => void;
  readonly disabled?: boolean;
}

export function Switch({ labelKey, checked, onCheckedChange, disabled }: SwitchProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <RadixSwitch.Root
        id={id}
        {...(checked === undefined ? {} : { checked })}
        {...(onCheckedChange === undefined ? {} : { onCheckedChange })}
        disabled={disabled}
        className={`bg-surface-sunken border-hairline data-[state=checked]:bg-ink-primary relative h-4 w-8 rounded-full border transition-colors disabled:opacity-50 ${FOCUS_RING}`}
      >
        {/* Logical inset, never `left`: G6 requires logical properties from day one. */}
        <RadixSwitch.Thumb className="bg-surface-raised block size-3 rounded-full transition-transform data-[state=checked]:translate-x-4" />
      </RadixSwitch.Root>
      <RadixLabel.Root htmlFor={id} className="text-body text-ink-primary">{t(labelKey)}</RadixLabel.Root>
    </div>
  );
}

// ── slider ───────────────────────────────────────────────────────────────────

export interface SliderProps {
  readonly labelKey: string;
  readonly unitKey?: string;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly value?: readonly number[];
  readonly onValueChange?: (value: number[]) => void;
}

export function Slider({ labelKey, unitKey, min, max, step = 1, value, onValueChange }: SliderProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <FieldShell labelKey={labelKey} {...(unitKey === undefined ? {} : { unitKey })}>
      {({ controlId }) => (
        <RadixSlider.Root
          id={controlId}
          min={min}
          max={max}
          step={step}
          {...(value === undefined ? {} : { value: [...value] })}
          {...(onValueChange === undefined ? {} : { onValueChange })}
          className="relative flex h-6 w-full items-center"
        >
          <RadixSlider.Track className="bg-surface-sunken border-hairline relative h-1 w-full grow rounded-chip border">
            <RadixSlider.Range className="bg-ink-primary absolute h-full rounded-chip" />
          </RadixSlider.Track>
          {(value ?? [min]).map((v, i) => (
            <RadixSlider.Thumb
              key={i}
              className={`bg-surface-raised border-ink-primary block size-3 rounded-full border-2 ${FOCUS_RING}`}
              // The accessible name goes on the THUMB, which is the element carrying
              // role="slider". Putting it on the Root — a plain span — leaves the slider
              // itself unnamed, which axe reports as aria-input-field-name, and a screen
              // reader announces "slider" with no indication of what it adjusts.
              aria-label={t(labelKey)}
              aria-valuetext={displayNumberFormat(i18n.language).format(v)}
            />
          ))}
        </RadixSlider.Root>
      )}
    </FieldShell>
  );
}
